import i18n from '@risksmart-app/i18n/src/i18n';
import type { GetIndicatorsQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMemo } from 'react';
import {
  useGetIndicatorRegister,
  type UseGetIndicatorRegisterArgs,
} from 'src/hooks/queries/indicator/useGetIndicatorRegister';
import { useGetRisksFlat } from 'src/hooks/queries/risk/useGetRisksFlat';
import { useLabelledFields as useIndicatorLabelledFields } from 'src/pages/indicators/useLabelledFields';

import { useCommonLookupLazy } from '@/hooks/useCommonLookupLazy';
import { getFriendlyId } from '@/utils/friendlyId';
import type { StatefulTableOptions } from '@/utils/table/hooks/useGetStatelessTableProps';
import type { TableFields, TableRecord } from '@/utils/table/types';

import { UNRATED } from '../../gigawidget/types';
import { createDataSource } from '../createDataSource';
import { defaultClickthroughFilter } from '../dataSourceHelpers';
import {
  deriveRiskTierCategoryAndEntity,
  type IndicatorRowSlice,
  indicatorSideCategoryGetters,
  indicatorSideFieldConfig,
  prefixedLabel,
  resolveIndicatorSideRow,
  riskTierCategoryAndEntityFieldConfig,
  riskTierCategoryAndEntityGetters,
  type RiskTierCategorySlice,
  simpleField,
  useRelationshipTableProps,
} from './relationship-helpers';

export type IndicatorRiskRow = RiskTierCategorySlice &
  TableRecord &
  IndicatorRowSlice & {
    Id: string;
    RiskId: string;
    RiskFriendlyId: string;
    RiskTitle: string;
    RiskTier: string;
    RiskStatus: string;
    RiskTreatment: string;
  };

export default createDataSource<
  IndicatorRiskRow,
  UseGetIndicatorRegisterArgs,
  GetIndicatorsQuery
>({
  group: 'relationship',
  hasAccess: () => true,
  parentTypes: [Parent_Type_Enum.Risk, Parent_Type_Enum.Indicator],
  useWidgetQuery: useGetIndicatorRegister,
  useTablePropsHook: (
    data: GetIndicatorsQuery | undefined,
    options: StatefulTableOptions<IndicatorRiskRow>
  ) => {
    const fields = useMemo<TableFields<IndicatorRiskRow>>(
      () => ({
        RiskFriendlyId: simpleField(
          prefixedLabel('risk_one', i18n.t('columns.id'))
        ),
        RiskTitle: simpleField(
          prefixedLabel('risk_one', i18n.t('risks.columns.risk_name'))
        ),
        RiskTier: simpleField(
          prefixedLabel('risk_one', i18n.t('risks.columns.risk_tier'))
        ),
        RiskStatus: simpleField(
          prefixedLabel('risk_one', i18n.t('risks.columns.risk_status'))
        ),
        RiskTreatment: simpleField(
          prefixedLabel('risk_one', i18n.t('risks.columns.risk_treatment'))
        ),
        ...riskTierCategoryAndEntityFieldConfig<IndicatorRiskRow>(),
        ...indicatorSideFieldConfig<IndicatorRiskRow>(),
      }),
      []
    );

    const indicatorsLabelled = useIndicatorLabelledFields(data?.indicator);
    const { getByValue: commonLookup } = useCommonLookupLazy();
    const { data: riskData, loading: risksLoading } = useGetRisksFlat({
      queryArgs: {},
    });

    const joinedData = useMemo<IndicatorRiskRow[] | undefined>(() => {
      if (!indicatorsLabelled || !riskData?.risk) {
        return undefined;
      }

      const riskMap = new Map(riskData.risk.map((r) => [r.Id, r]));

      return indicatorsLabelled.flatMap((indicator) =>
        (indicator.parents ?? [])
          .filter(
            (p) => p.parent?.ObjectType === Parent_Type_Enum.Risk && p.parent.Id
          )
          .map((parent) => {
            const risk = riskMap.get(parent.parent!.Id);

            return {
              ...deriveRiskTierCategoryAndEntity(risk, riskMap),
              Id: `${parent.parent!.Id}_${indicator.Id}`,
              RiskId: parent.parent!.Id,
              RiskFriendlyId: risk?.SequentialId
                ? getFriendlyId(Parent_Type_Enum.Risk, risk.SequentialId)
                : '',
              // Partner title falls back to '' rather than a raw-joined value
              // from the indicator register — the register eager-loads
              // parent.risk.Title via a SQL join that is not re-filtered by
              // the partner entity's permission scope, so using it here
              // would leak titles the user can't otherwise see.
              RiskTitle: risk?.Title ?? '',
              RiskTier: commonLookup('tiers', risk?.Tier)?.label ?? '',
              RiskStatus: commonLookup('statuses', risk?.Status)?.label ?? '',
              RiskTreatment:
                commonLookup('treatments', risk?.Treatment)?.label ?? '',
              ...resolveIndicatorSideRow(indicator),
            };
          })
      );
    }, [indicatorsLabelled, riskData?.risk, commonLookup]);

    const tableProps = useRelationshipTableProps(
      joinedData,
      fields,
      i18n.t('risk_indicator_other'),
      options
    );

    // The join stays undefined until the secondary risks query lands —
    // report that window as loading rather than "no data" (RSP-6539).
    return { ...tableProps, isEnrichmentLoading: risksLoading };
  },
  entityNamePlural: 'risk_indicator_other',
  entityNameSingular: 'risk_indicator_one',
  fields: 'riskIndicators.fields',
  dashboardFilterConfig: {},
  categoryGetters: [
    {
      id: 'riskFriendlyId',
      entityGroup: 'risk_one',
      name: () => prefixedLabel('risk_one', i18n.t('columns.id')),
      categoryGetter: (item: IndicatorRiskRow) =>
        item.RiskFriendlyId || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('RiskFriendlyId'),
    },
    {
      id: 'riskTitle',
      fieldId: 'Title',
      entityGroup: 'risk_one',
      name: () => prefixedLabel('risk_one', i18n.t('risks.columns.risk_name')),
      categoryGetter: (item: IndicatorRiskRow) => item.RiskTitle || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('RiskTitle'),
    },
    {
      id: 'riskTier',
      fieldId: 'Tier',
      entityGroup: 'risk_one',
      name: () => prefixedLabel('risk_one', i18n.t('risks.columns.risk_tier')),
      categoryGetter: (item: IndicatorRiskRow) => item.RiskTier || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('RiskTier'),
    },
    {
      id: 'riskStatus',
      fieldId: 'Status',
      entityGroup: 'risk_one',
      name: () =>
        prefixedLabel('risk_one', i18n.t('risks.columns.risk_status')),
      categoryGetter: (item: IndicatorRiskRow) => item.RiskStatus || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('RiskStatus'),
    },
    {
      id: 'riskTreatment',
      fieldId: 'Treatment',
      entityGroup: 'risk_one',
      name: () =>
        prefixedLabel('risk_one', i18n.t('risks.columns.risk_treatment')),
      categoryGetter: (item: IndicatorRiskRow) => item.RiskTreatment || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('RiskTreatment'),
    },
    ...riskTierCategoryAndEntityGetters<IndicatorRiskRow>(),
    ...indicatorSideCategoryGetters<IndicatorRiskRow>(),
  ],
});
